import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * VDM statuses the poll looks for a response for.
 *
 * String literals rather than an import: this package must not depend on the
 * backend's Sequelize models. `vdmPollStatusParity.test.ts` in the backend
 * asserts these match the real VDMStatus values, so the two cannot drift.
 */
export const VDM_POLL_STATUS_VALUES = [
  "created",
  "pending_as400_edit",
  "error_creating",
  "error_editing",
] as const;

export const pollVdmAs400ResponsesConfigSchema = z.object({
  statuses: z
    .array(z.enum(VDM_POLL_STATUS_VALUES))
    .min(1)
    // Only the two states genuinely awaiting AS400. An ERROR_* VDM has already
    // had its answer — the error IS the answer — so polling it re-lists its
    // division folder every run for a response that will never arrive.
    .default(["created", "pending_as400_edit"])
    .describe(
      "Which VDM statuses to check for an AS400 response. Defaults to the two " +
        "states still awaiting one. Add the error states only to deliberately " +
        "re-read AS400 for VDMs it already rejected.",
    ),
});

export type PollVdmAs400ResponsesConfig = z.infer<
  typeof pollVdmAs400ResponsesConfigSchema
>;

/**
 * Reads AS400's answer to the VDMs we uploaded, and applies it.
 *
 * We write a VDM request to blob storage as `I1_GNTVDM_*` (create) or
 * `I2_GNTVDM_*` (edit); AS400 answers in the same folder with the matching
 * `O1_`/`O2_` file. A confirmation carries the AS400-assigned VDM number and
 * the tax amounts; a rejection carries the reason.
 *
 * This is the CHECKER, and only the checker. Until it existed, those response
 * files were read ONLY by the VDM list page's on-mount refresh — so a VDM
 * stayed `created`, and every EDI 844 submission waiting on its number stayed
 * `pending_vdm` with no GEN844 on the wire, until a person happened to open
 * that page.
 *
 * Finalising those submissions stays where it already is: the
 * `pollPendingVdmSubmissions` sweep, plus the immediate hand-off this step
 * triggers for the VDMs it just resolved. Deliberately NOT folded in here —
 * that sweep is live and owns the deferred GEN844 upload and the supplier
 * email, and a workflow that has to be created before it runs is the wrong
 * place to put work that is already running.
 */
export const pollVdmAs400ResponsesStep = defineStepConfig({
  type: "poll_vdm_as400_responses",
  name: "Poll AS400 VDM Responses",
  description:
    "Checks blob storage for AS400's VDM response files (O1 create / O2 edit) " +
    "and applies them: a confirmed VDM gets its AS400 number and tax amounts, " +
    "a rejected one is marked errored with the reason AS400 gave. Both log an " +
    "AS400_RESPONDED activity, and any 844 submission whose VDMs just resolved " +
    "has its deferred upload/email released.",
  category: "Integration",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({}),
  outputSchema: z.object({
    checkedCount: z
      .number()
      .describe("Pending VDMs we looked for a response for"),
    updatedCount: z
      .number()
      .describe("VDMs AS400 answered — confirmed or rejected"),
  }),
  configSchema: pollVdmAs400ResponsesConfigSchema,
  emits: [] as const,
  execution: {
    // Generous: a first run after a backlog lists every division folder and
    // may apply many response files.
    startToCloseTimeout: "10 minutes",
    // Applying a response is idempotent per (VDM, file) — each VDM records the
    // response key it applied — so a retry cannot double-apply.
    retry: { maximumAttempts: 2 },
  },
});
